'use client';

import { useEffect, useState, useCallback } from 'react';
import { ProtectedRoute } from '@/lib/auth';
import Link from 'next/link';

interface Preferences {
  newsletterSubscribed: boolean;
  emailFrequency: string;
  preferredTopics: string[];
  emailFormat: string;
  theme: string;
  push_daily_sync: boolean;
  push_incidents: boolean;
  push_outages: boolean;
  push_streak_reminders: boolean;
  push_badge_earned: boolean;
  push_learning_milestones: boolean;
  push_weekly_summary: boolean;
  push_quiet_start: string;
  push_quiet_end: string;
}

type PushState = 'unsupported' | 'denied' | 'prompt' | 'subscribed' | 'unsubscribed' | 'loading';

function isNativePlatform() {
  if (typeof window === 'undefined') return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cap = (window as any).Capacitor;
  return cap?.isNativePlatform?.() === true;
}

const AVAILABLE_TOPICS = [
  'Leadership & People Management',
  'System Architecture & Design',
  'Hiring & Interview Questions',
  'Career Growth (IC to CTO)',
  'DevOps & Infrastructure',
  'Security & Compliance',
  'Tooling & Productivity',
  'Metrics & Analytics'
];

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function SettingsContent() {
  const [preferences, setPreferences] = useState<Preferences>({
    newsletterSubscribed: true,
    emailFrequency: 'weekly',
    preferredTopics: [],
    emailFormat: 'summary',
    theme: 'dark',
    push_daily_sync: false,
    push_incidents: false,
    push_outages: false,
    push_streak_reminders: true,
    push_badge_earned: true,
    push_learning_milestones: true,
    push_weekly_summary: false,
    push_quiet_start: '22:00',
    push_quiet_end: '07:00',
  });
  const [native] = useState(() => isNativePlatform());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [pushState, setPushState] = useState<PushState>('loading');

  // Detect push support and current subscription state
  const detectPushState = useCallback(async () => {
    // Native push detection via Capacitor
    if (native) {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const permResult = await PushNotifications.checkPermissions();
        if (permResult.receive === 'denied') {
          setPushState('denied');
        } else if (permResult.receive === 'granted') {
          setPushState('subscribed');
        } else {
          setPushState('unsubscribed');
        }
      } catch {
        setPushState('unsupported');
      }
      return;
    }

    // Web push detection
    if (!('PushManager' in window) || !('serviceWorker' in navigator)) {
      setPushState('unsupported');
      return;
    }

    const permission = Notification.permission;
    if (permission === 'denied') {
      setPushState('denied');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setPushState(subscription ? 'subscribed' : 'unsubscribed');
    } catch {
      setPushState('unsubscribed');
    }
  }, [native]);

  const handleEnablePush = async () => {
    setPushState('loading');
    try {
      if (native) {
        // Native push via Capacitor
        const { registerNativePush } = await import('@/lib/native-push');
        await registerNativePush();
        setPreferences(prev => ({ ...prev, push_daily_sync: true }));
        setPushState('subscribed');
        return;
      }

      // Web push flow
      // Fetch VAPID public key
      const keyRes = await fetch('/api/public/push/vapid-key');
      if (!keyRes.ok) throw new Error('Push not available');
      const { publicKey } = await keyRes.json();

      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setPushState(permission === 'denied' ? 'denied' : 'prompt');
        return;
      }

      // Subscribe via PushManager
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey).buffer as ArrayBuffer,
      });

      const subJson = subscription.toJSON();

      // Save subscription to server
      await fetch('/api/secure/push/subscribe', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
          deviceLabel: navigator.userAgent.slice(0, 100),
        }),
      });

      // Enable daily sync push preference
      setPreferences(prev => ({ ...prev, push_daily_sync: true }));
      await fetch('/api/secure/preferences', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pushDailySyncEnabled: true }),
      });

      setPushState('subscribed');
    } catch (error) {
      console.error('Failed to enable push:', error);
      await detectPushState();
    }
  };

  const handleDisablePush = async () => {
    setPushState('loading');
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Remove from server
        await fetch('/api/secure/push/subscribe', {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        });
        await subscription.unsubscribe();
      }

      setPushState('unsubscribed');
    } catch (error) {
      console.error('Failed to disable push:', error);
      await detectPushState();
    }
  };

  const handleToggleDailySyncPush = async (enabled: boolean) => {
    setPreferences(prev => ({ ...prev, push_daily_sync: enabled }));
    await fetch('/api/secure/preferences', {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pushDailySyncEnabled: enabled }),
    });
  };

  const handleToggleNotifPref = async (key: keyof Preferences, value: boolean | string) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    await fetch('/api/secure/preferences', {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [key]: value }),
    });
  };

  useEffect(() => {
    async function fetchPreferences() {
      try {
        const response = await fetch('/api/secure/preferences', {
          credentials: 'include'
        });

        if (response.ok) {
          const result = await response.json();
          const d = result.data;
          setPreferences({
            newsletterSubscribed: d.newsletter_subscribed ?? true,
            emailFrequency: d.email_frequency ?? 'weekly',
            preferredTopics: d.preferred_topics ?? [],
            emailFormat: d.email_format ?? 'summary',
            theme: d.theme ?? 'dark',
            push_daily_sync: d.push_daily_sync ?? false,
            push_incidents: d.push_incidents ?? false,
            push_outages: d.push_outages ?? false,
            push_streak_reminders: d.push_streak_reminders ?? true,
            push_badge_earned: d.push_badge_earned ?? true,
            push_learning_milestones: d.push_learning_milestones ?? true,
            push_weekly_summary: d.push_weekly_summary ?? false,
            push_quiet_start: d.push_quiet_start ?? '22:00',
            push_quiet_end: d.push_quiet_end ?? '07:00',
          });
        }
      } catch (error) {
        console.error('Failed to load preferences:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchPreferences();
    detectPushState();
  }, [detectPushState]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      const response = await fetch('/api/secure/preferences', {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });

      if (response.ok) {
        setSaveMessage('Settings saved successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setSaveMessage('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleTopic = (topic: string) => {
    setPreferences(prev => ({
      ...prev,
      preferredTopics: prev.preferredTopics.includes(topic)
        ? prev.preferredTopics.filter(t => t !== topic)
        : [...prev.preferredTopics, topic]
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link
              href="/dashboard"
              className="text-accent hover:text-foreground transition-colors"
            >
              ← Back to Dashboard
            </Link>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground">
            Settings
          </h1>
          <p className="text-foreground-secondary mt-2">
            Manage your preferences and account settings
          </p>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              saveMessage.includes('success')
                ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                : 'bg-red-500/10 border border-red-500/30 text-red-400'
            }`}
          >
            {saveMessage}
          </div>
        )}

        <div className="space-y-8">
          {/* Newsletter Preferences */}
          <div className="bg-surface border border-border rounded-lg p-6">
            <h2 className="text-2xl font-bold text-foreground mb-6">
              Newsletter Preferences
            </h2>

            <div className="space-y-6">
              {/* Newsletter Subscription */}
              <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
                <input
                  type="checkbox"
                  id="newsletter-subscribed"
                  checked={preferences.newsletterSubscribed}
                  onChange={(e) =>
                    setPreferences({ ...preferences, newsletterSubscribed: e.target.checked })
                  }
                  className="mt-1 w-5 h-5 rounded border-border bg-background text-accent focus:ring-2 focus:ring-ring/50"
                />
                <div>
                  <span className="font-semibold text-foreground">
                    Subscribe to Newsletter
                  </span>
                  <p className="text-sm text-foreground-secondary mt-1">
                    Receive our weekly digest with insights, frameworks, and tools
                  </p>
                </div>
              </label>

              {/* Email Frequency */}
              <div>
                <label className="block font-semibold text-foreground mb-2">
                  Email Frequency
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'daily', label: 'Daily', desc: 'New content as it\'s published' },
                    { value: 'weekly', label: 'Weekly', desc: 'Monday morning digest (recommended)' },
                    { value: 'monthly', label: 'Monthly', desc: 'Monthly roundup' },
                    { value: 'never', label: 'Never', desc: 'Unsubscribe from all emails' }
                  ].map(option => (
                    <label
                      key={option.value}
                      className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-background/50"
                    >
                      <input
                        type="radio"
                        name="email-frequency"
                        value={option.value}
                        checked={preferences.emailFrequency === option.value}
                        onChange={(e) =>
                          setPreferences({ ...preferences, emailFrequency: e.target.value })
                        }
                        className="mt-1 text-accent focus:ring-ring"
                      />
                      <div>
                        <div className="text-foreground">{option.label}</div>
                        <div className="text-sm text-foreground-tertiary">{option.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Email Format */}
              <div>
                <label className="block font-semibold text-foreground mb-2">
                  Email Format
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'full', label: 'Full Articles', desc: 'Complete articles in email' },
                    { value: 'summary', label: 'Summaries + Links', desc: 'Short previews with links (recommended)' },
                    { value: 'links', label: 'Links Only', desc: 'Minimal, just the links' }
                  ].map(option => (
                    <label
                      key={option.value}
                      className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-background/50"
                    >
                      <input
                        type="radio"
                        name="email-format"
                        value={option.value}
                        checked={preferences.emailFormat === option.value}
                        onChange={(e) =>
                          setPreferences({ ...preferences, emailFormat: e.target.value })
                        }
                        className="mt-1 text-accent focus:ring-ring"
                      />
                      <div>
                        <div className="text-foreground">{option.label}</div>
                        <div className="text-sm text-foreground-tertiary">{option.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Preferred Topics */}
              <div>
                <label className="block font-semibold text-foreground mb-2">
                  Preferred Topics
                </label>
                <p className="text-sm text-foreground-secondary mb-4">
                  Select topics you&apos;re most interested in for personalized content
                </p>
                <div className="grid md:grid-cols-2 gap-3">
                  {AVAILABLE_TOPICS.map(topic => (
                    <label
                      key={topic}
                      className="flex items-center gap-3 min-h-[44px] cursor-pointer p-3 rounded-lg hover:bg-background/50"
                    >
                      <input
                        type="checkbox"
                        checked={preferences.preferredTopics.includes(topic)}
                        onChange={() => toggleTopic(topic)}
                        className="w-5 h-5 rounded border-border bg-background text-accent focus:ring-2 focus:ring-ring/50"
                      />
                      <span className="text-foreground">{topic}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Appearance */}
          <div className="bg-surface border border-border rounded-lg p-6">
            <h2 className="text-2xl font-bold text-foreground mb-6">
              Appearance
            </h2>

            <div>
              <label className="block font-semibold text-foreground mb-2">
                Theme
              </label>
              <div className="space-y-2">
                {[
                  { value: 'dark', label: 'Dark', desc: 'Navy background (current)' },
                  { value: 'light', label: 'Light', desc: 'Light background (coming soon)' }
                ].map(option => (
                  <label
                    key={option.value}
                    className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-background/50"
                  >
                    <input
                      type="radio"
                      name="theme"
                      value={option.value}
                      checked={preferences.theme === option.value}
                      onChange={(e) =>
                        setPreferences({ ...preferences, theme: e.target.value })
                      }
                      disabled={option.value === 'light'}
                      className="mt-1 text-accent focus:ring-ring disabled:opacity-50"
                    />
                    <div>
                      <div className="text-foreground">{option.label}</div>
                      <div className="text-sm text-foreground-tertiary">{option.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Push Notifications */}
          <div className="bg-surface border border-border rounded-lg p-6">
            <h2 className="text-2xl font-bold text-foreground mb-6">
              Push Notifications
            </h2>

            {pushState === 'unsupported' ? (
              <p className="text-foreground-secondary">
                Push notifications are not supported {native ? 'on this device' : 'in this browser'}.
              </p>
            ) : pushState === 'denied' ? (
              <div>
                <p className="text-foreground-secondary mb-2">
                  Notifications are blocked. To enable them, update your {native ? 'device' : 'browser'}&apos;s notification settings for this {native ? 'app' : 'site'}.
                </p>
              </div>
            ) : pushState === 'loading' ? (
              <p className="text-foreground-secondary">Checking notification status...</p>
            ) : pushState === 'subscribed' ? (
              <div className="space-y-6">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="font-semibold text-foreground">Notifications enabled on this device</div>
                    <p className="text-sm text-foreground-secondary mt-1">
                      You&apos;ll receive push notifications on this device.
                    </p>
                  </div>
                  {!native && (
                    <button
                      onClick={handleDisablePush}
                      className="text-sm text-foreground-secondary hover:text-foreground transition-colors"
                    >
                      Disable
                    </button>
                  )}
                </div>

                <div className="border-t border-border pt-4">
                  <label className="block font-semibold text-foreground mb-3">
                    Notification Categories
                  </label>
                  <div className="space-y-3">
                    {([
                      { key: 'push_daily_sync' as const, label: 'Daily Sync', desc: 'Morning CTO briefing notification' },
                      { key: 'push_streak_reminders' as const, label: 'Streak Reminders', desc: 'Daily reminder to keep your streak alive' },
                      { key: 'push_badge_earned' as const, label: 'Badge Earned', desc: 'When you earn a new achievement badge' },
                      { key: 'push_learning_milestones' as const, label: 'Learning Milestones', desc: 'Progress updates on learning paths' },
                      { key: 'push_incidents' as const, label: 'Incidents', desc: 'SEV1/SEV2 incident alerts from Command Center' },
                      { key: 'push_outages' as const, label: 'Cloud Outages', desc: 'Alerts for watched cloud providers' },
                      { key: 'push_weekly_summary' as const, label: 'Weekly Summary', desc: 'Weekly activity recap every Monday' },
                    ]).map(item => (
                      <label key={item.key} className="flex items-start gap-3 min-h-[44px] cursor-pointer">
                        <input
                          type="checkbox"
                          id={item.key}
                          checked={preferences[item.key] as boolean}
                          onChange={(e) => {
                            if (item.key === 'push_daily_sync') {
                              handleToggleDailySyncPush(e.target.checked);
                            } else {
                              handleToggleNotifPref(item.key, e.target.checked);
                            }
                          }}
                          className="mt-1 w-5 h-5 rounded border-border bg-background text-accent focus:ring-2 focus:ring-ring/50"
                        />
                        <div>
                          <span className="font-semibold text-foreground">
                            {item.label}
                          </span>
                          <p className="text-sm text-foreground-secondary mt-1">
                            {item.desc}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Quiet Hours */}
                {native && (
                  <div className="border-t border-border pt-4">
                    <label className="block font-semibold text-foreground mb-2">
                      Quiet Hours
                    </label>
                    <p className="text-sm text-foreground-secondary mb-3">
                      Non-urgent notifications will be silenced during these hours.
                    </p>
                    <div className="flex items-center gap-3">
                      <div>
                        <label htmlFor="quiet-start" className="text-sm text-foreground-secondary">From</label>
                        <input
                          id="quiet-start"
                          type="time"
                          value={preferences.push_quiet_start}
                          onChange={(e) => handleToggleNotifPref('push_quiet_start', e.target.value)}
                          className="block mt-1 px-3 py-1.5 rounded-lg border border-border bg-background text-foreground text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="quiet-end" className="text-sm text-foreground-secondary">Until</label>
                        <input
                          id="quiet-end"
                          type="time"
                          value={preferences.push_quiet_end}
                          onChange={(e) => handleToggleNotifPref('push_quiet_end', e.target.value)}
                          className="block mt-1 px-3 py-1.5 rounded-lg border border-border bg-background text-foreground text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <p className="text-foreground-secondary mb-4">
                  Enable push notifications to get alerted when new content is published, right on your device.
                </p>
                <button
                  onClick={handleEnablePush}
                  className="px-6 py-2 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-lg transition-colors"
                >
                  Enable Push Notifications
                </button>
              </div>
            )}
          </div>

          {/* Account */}
          <div className="bg-surface border border-border rounded-lg p-6">
            <h2 className="text-2xl font-bold text-foreground mb-6">
              Account
            </h2>

            <div className="space-y-4">
              {!native && (
                <Link
                  href="/dashboard/billing"
                  className="block p-4 border border-border rounded-lg hover:border-border-muted0 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-foreground">Billing & Subscription</div>
                      <div className="text-sm text-foreground-secondary">Manage your plan and payment methods</div>
                    </div>
                    <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              )}

              <div className="p-4 border border-border rounded-lg">
                <div className="font-semibold text-foreground mb-2">Privacy & Data</div>
                <div className="text-sm text-foreground-secondary mb-4">
                  Your data is encrypted and never shared with third parties. See our{' '}
                  <Link href="/privacy" className="text-accent hover:underline">
                    Privacy Policy
                  </Link>.
                </div>
                <button className="text-sm text-accent hover:text-foreground transition-colors">
                  Export My Data
                </button>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-8 py-3 bg-accent hover:bg-accent-hover disabled:bg-accent-muted0 text-accent-foreground font-semibold rounded-lg transition-colors disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
            <Link
              href="/dashboard"
              className="px-8 py-3 border border-border hover:border-border-muted0 text-foreground font-semibold rounded-lg transition-colors"
            >
              Cancel
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  return (
    <ProtectedRoute>
      <SettingsContent />
    </ProtectedRoute>
  );
}
